/**
 * Request/response contracts for the Supabase Edge Functions — the single shape
 * both ends reference so the Deno handler and the app client can't drift. A
 * mismatch becomes a compile error on whichever side changed.
 *
 * Self-contained with NO imports (exactly like `scrub.ts`) so the Deno edge
 * functions resolve it across the workspace by relative path
 * (`../../../packages/shared/src/contracts.ts`) while the app imports it via
 * `@rbf/shared`. Role fields use inline string-literal unions rather than
 * importing the `TemplateRole`/`AccessRole` enums from `./model` (which pulls in
 * zod) to keep that import-free property — they're structurally identical to the
 * enums, so they assign cleanly on the app side. Keep them in sync with the
 * enums in `model.ts`.
 */

/** Error body returned by every function on a non-2xx (see `_shared/cors.ts`). */
export interface EdgeErrorResponse {
  error: string;
}

// beta-signup — create an account during the private beta, gated by a secret code.
export interface BetaSignupRequest {
  code: string;
  email: string;
  password: string;
  displayName: string;
}
export interface BetaSignupResponse {
  ok: true;
}

// create-template-share — an admin mints a role-tagged invite link.
export interface CreateTemplateShareRequest {
  template_id: string;
  role: 'member' | 'editor' | 'admin';
}
export interface CreateTemplateShareResponse {
  token: string;
  role: 'member' | 'editor' | 'admin';
  template_id: string;
}

// redeem-template-share — a recipient opens an invite link.
export interface RedeemTemplateShareRequest {
  token: string;
}
export interface RedeemTemplateShareResponse {
  template_id: string;
  // The caller's effective role; `owner` when they opened their own link.
  role: 'owner' | 'admin' | 'editor' | 'member';
}

// delete-account — permanently delete the caller's account (resolved from the JWT).
export type DeleteAccountRequest = Record<string, never>;
export interface DeleteAccountResponse {
  ok: true;
}
